<script setup lang="ts">
import type { PropType } from 'vue'
import config from '~/common/config'
import { computedStatusColor } from '~/utils/utils-js'
import { getFloorTypeIndexUrl } from '~/composables/useRouteAddress'
import { getFloorDiscountList } from '~/server/newhouse'
import type { FloorDiscountListVO, FloorListItem } from '~/server/newhouse/interface'
import { useCityInfoStore } from '~/stores/useCityInfo'
import { useWechatStore } from '~/stores/useWeChat'

const props = defineProps({
  data: {
    type: Object as () => FloorListItem,
    default: () => {},
  },
  type: {
    type: Array as PropType<number[]>,
    default: () => [0], // 0为默认普通楼栋数据、1为品质榜
  },
})

const emits = defineEmits(['openDiscountModal'])

const cityInfoStore = useCityInfoStore()
const discountList = ref<FloorDiscountListVO[]>()
const discountItem = computed(() => {
  return (
    discountList.value?.find(d => d.discountId === props.data.discountId)
    || discountList.value?.[0]
  )
})

const imgSrc = computed(() =>
  props.data?.ad === 1
    ? props.data?.pcAdUrl || props.data?.photo || config.DEFAULT_HOUSE_PIC
    : props.data?.photo || config.DEFAULT_HOUSE_PIC,
)

const saleStatus = computed(() => {
  const { saleStatusIdList, saleStatusList } = props.data
  if (
    saleStatusIdList
    && saleStatusIdList[0]
    && saleStatusList
    && saleStatusList[0]
  ) {
    return {
      saleStatusId: saleStatusIdList[0],
      saleStatus: saleStatusList[0],
    }
  }
  else {
    return undefined
  }
})

const statusColor = computed(() => computedStatusColor(saleStatus.value))

const floorLabelList = computed(
  () => props.data.floorLabelList?.filter(Boolean) || [],
)

function getNearbyDescribe(type = 1) {
  const { cityRoundList } = props.data
  // cityRoundList：[{count, name}]
  // 将cityRoundList中的数据按照：
  // type === 1：附近地铁站口1个、公交站10个
  // type === 2：附近5家商业体、2家医疗机构、1个公园、2家医疗机构
  // type === 3:附近5所幼儿园、5所小学、5所中学
  // 拼接出来
  const info: string[] = []
  if (type === 1) {
    cityRoundList
      ?.filter((item: any) => item.count > 0)
      .forEach((item: any) => {
        if (item.name === '地铁站')
          info.push(`地铁站口(${item.count})`)
        else if (item.name === '公交车站')
          info.push(`公交站(${item.count})`)
      })
  }
  else if (type === 2) {
    cityRoundList
      ?.filter((item: any) => item.count > 0)
      .forEach((item: any) => {
        if (item.name === '商业')
          info.push(`商业体(${item.count})`)
        else if (item.name === '医疗')
          info.push(`医疗机构(${item.count})`)
        else if (item.name === '公园')
          info.push(`公园(${item.count})`)
      })
  }
  else if (type === 3) {
    cityRoundList
      ?.filter((item: any) => item.count > 0)
      .forEach((item: any) => {
        if (item.name === '幼儿园')
          info.push(`幼儿园(${item.count})`)
        else if (item.name === '中学')
          info.push(`中学(${item.count})`)
        else if (item.name === '小学')
          info.push(`小学(${item.count})`)
      })
  }
  return info.join('、')
}

function openModal() {
  emits('openDiscountModal', props.data, discountItem.value)
}
function handleDiscount() {
  if (discountItem.value) {
    openModal()
  }
  else {
    getFloorDiscountList({
      cityId: cityInfoStore.city,
      floorId: props.data.floorId,
    }).then((res) => {
      if (res.code === 0) {
        discountList.value = res.dataBody
        openModal()
      }
    })
  }
}

/**
 * 跳转到对应的榜单
 * ! a(nuxtlink) 标签中 a(nuxtlink) 标签 循环时，会造成水合问题
 */
function handleJumpRank(typeId) {
  window.open(getRankListUrl(typeId), '_blank')
  // router.push(getRankListUrl(props.data.rankListTypeId))
}

// 1：人气榜、2：热搜榜、3：荣誉榜、4：品质榜、5：关注榜
const rankTypeList = {
  1: '人气榜',
  2: '热搜榜',
  3: '荣誉榜',
  4: '品质榜',
  5: '关注榜',
}
// 优惠>荣誉榜>热搜榜>人气榜>品质榜>关注榜

//  随机榜单数据
const rank = computed(() => {
  if (props?.data?.floorRankList && props?.data?.floorRankList.length) {
    return props?.data?.floorRankList[
      Math.floor(Math.random() * props?.data?.floorRankList.length)
    ]
  }
  else {
    return null
  }
  // Start of Selection
})

const wechatStore = useWechatStore()
const { commonCollect } = userInfoAction()

const storageAdviser = wechatStore.getAdviser(props.data.floorId as number)

const adviserInfo = ref<any>(null)

onMounted(() => {
  if (storageAdviser) {
    adviserInfo.value = storageAdviser
  }
  else {
    const { adviserList } = props.data
    if (adviserList && adviserList.length) {
      adviserInfo.value = adviserList[Math.floor(Math.random() * adviserList.length)]
    }
    else {
      adviserInfo.value = null
    }
  }
})

const configMap = inject<any>('configMap')

function handleAdviser(e: MouseEvent) {
// 阻止父级的a标签跳转
  e.preventDefault()

  // 有缓存置业顾问
  const { adviserId } = adviserInfo.value

  commonCollect({
    configId: configMap.value?.pcPropertyConsultantHome?.configId,
    cityId: props.data.cityId as number,
    floorId: props.data.floorId as number,
    source: '新房/PC/楼盘首页',
    sourceTypeId: 1,
    adviserId,
    message: '我刚浏览了这个楼盘，请问可以介绍下吗？',
  })
}
</script>

<template>
  <a :href="getFloorTypeIndexUrl(data)" target="_blank">
    <div class="floor-item">
      <div class="pic">
        <div class="floor-img" :class="data?.rank ? 'award-photo-frame' : ''">
          <img
            :src="$getImageSize(imgSrc, 'c460x340')"
            :alt="`${data?.floor}的封面图`"
          >
        </div>
        <div class="video-and-vr">
          <img
            v-if="data?.hasVr === 1"
            class="video-icon"
            src="@/assets/images/vr-icon.png"
            alt=""
          >
          <img
            v-if="data?.hasVideo === 1"
            class="vr-icon"
            src="@/assets/images/video-icon.png"
            alt=""
          >
        </div>
        <!--      开盘日期 -->
        <a
          v-if="data?.rank && type.includes(0)"
          class="ranking-logo"
          :href="getRankListUrl(data?.rankListTypeId)"
          target="_blank"
        >
          <div style="z-index: 2; position: relative">
            <img
              src="@/assets/images/rank-logo2.png"
              style="width: 8.5px; height: 11px"
              alt=""
            >
            <span style="color: #fff2b0">{{ data?.rankYear }}</span>
            {{ rankTypeList[data?.rankListTypeId] }}
          </div>
          <div class="end" />
        </a>
        <div
          v-else-if="data?.openingDate && type.includes(0)"
          class="icon opening-date"
        >
          {{ data?.openingDate }}<span style="font-size: 10px">月开</span>
        </div>
        <!--      品质榜 -->
        <div
          v-else-if="data?.totalPoint && type.includes(1)"
          class="icon quality"
        >
          {{ data?.totalPoint }}
        </div>
        <!--      热搜、人气榜等 -->
        <div v-else-if="data?.top && type.includes(2)" class="icon top">
          <span class="top-icon">TOP</span>{{ data?.top }}
        </div>
        <div v-show="data?.ad === 1" class="certificate">
          {{ data?.licence }}
        </div>
        <div v-show="data?.ad === 1" class="adTag">广告</div>
      </div>
      <div class="left-info">
        <h3 class="title">
          {{ data?.floor }}
        </h3>
        <div class="text">
          <EnvironmentOutlined class="icon" />
          {{
            [data?.areaName, data?.zone, data?.address]
              .filter(Boolean)
              .join('/')
          }}
        </div>
        <div class="text">
          <HomeOutlined class="icon" /> 建面：{{
            data?.modelAreaLow && data?.modelAreaHigh
              ? `${data?.modelAreaLow}~${data?.modelAreaHigh}`
              : '-'
          }}㎡&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;户型：{{
            data?.houseModelInfo ? `${data?.houseModelInfo}` : '-'
          }}
        </div>
        <div class="text chat">
          <!--        <ScanOutlined class="icon" /> 微信扫码拨号 -->
          <WechatDial
            :floor-id="data?.floorId"
            :city-id="data?.cityId"
            :code-width="150"
          />
          <div
            v-if="data?.discount"
            class="rank discount"
            @click.prevent="handleDiscount"
          >
            <img src="@/assets/images/discount-icon.png" alt="">
            {{ data?.discount }}
          </div>
          <div
            v-else-if="rank?.title"
            class="rank"
            @click="handleJumpRank(rank?.typeId)"
          >
            <!--            <img src="@/assets/images/rank-icon.png" alt=""> {{ rank?.areaName }}{{ rank?.type }}{{ rank?.top ? `第${rank?.top}名` : '' }} -->
            <img src="@/assets/images/rank-icon.png" alt=""> {{ rank?.title }}
          </div>
          <div v-if="!data?.discount && !rank && data?.title" class="rank">
            <img src="@/assets/images/rank-icon.png" alt=""> {{ data?.title }}
          </div>
        </div>
        <div class="tag-box">
          <tag v-if="saleStatus" :color="statusColor">
            {{ saleStatus.saleStatus }}
          </tag>
          <tag v-for="item in data?.propertyType?.split(',')" :key="item">
            {{ item }}
          </tag>
          <tag v-for="item in floorLabelList" :key="item">
            {{ item }}
          </tag>
          &nbsp;
        </div>
      </div>
      <div class="right-info mt-5px">
        <like
          :is-favorite="data?.isFavorite"
          :data="data"
          :number-id="data?.floorId"
          :title="data?.floor"
          :text="['关注', '取消关注']"
        />
        <!-- 物业子类型价格展示 非 榜单列表 -->
        <template v-if="(data?.priceList ?? []).filter(i => !!i.price).length > 0 && !type.includes(1)">
          <div v-for="(item, idx) in (data?.priceList ?? []).filter(i => !!i.price).slice(0, 3)" :key="`price-${idx}`" :class="{ 'price-0': idx === 0 }" class="price">
            <span class="num">
              <span class="text-16px prefix">{{
                [item.pricePropertyType, item?.pricePrefix].filter(Boolean).join('')
              }}</span>
              <span class="font-bold n">{{ item?.price }}</span>
              <span class="text-16px prefix">{{ item?.priceSuffix }}</span>
            </span>
          </div>
        </template>
        <!-- 榜单 -->
        <div v-else-if="data?.price" class="price price-0">
          <span class="num">
            <span v-if="data?.price" class="text-16px prefix">{{ data?.propertyType }}{{ data?.pricePrefix }}</span><span class="font-bold n">{{ data?.price }}</span><span class="text-16px prefix">{{ data?.priceSuffix }}</span>
          </span>
        </div>
        <div v-else class="price">
          <span class="num">
            <span class="text-16px prefix">{{
              data?.pricePrefix
            }}</span>
          </span>
        </div>
        <div v-if="type.includes(1)" class="data-info mt-15px">
          <div class="rank-data">
            {{
              getNearbyDescribe(1) ? `附近交通：${getNearbyDescribe(1)}` : ''
            }}
          </div>
          <div class="rank-data">
            {{
              getNearbyDescribe(2) ? `附近商业：${getNearbyDescribe(2)}` : ''
            }}
          </div>
          <div class="rank-data">
            {{
              getNearbyDescribe(3) ? `附近教育：${getNearbyDescribe(3)}` : ''
            }}
          </div>
        </div>
        <!-- 置业顾问 -->
        <div v-if="adviserInfo" :key="adviserInfo.adviserId" class="adviser-box" @click="handleAdviser">
          <img class="avatar" :src="adviserInfo?.headPic" alt="">
          <span class="name">{{ adviserInfo?.trueName }}</span>
          <!-- 在线咨询 -->
          <span class="online-consult">在线咨询</span>
        </div>
      </div>
    </div>
  </a>
</template>

<style scoped lang="scss">
.award-photo-frame {
  background: linear-gradient(270deg, #f5b752 0%, #efd08f 99%);
  //width: 230px;
  //height: 170px;
  border-radius: 10px;
  padding: 2px;
  box-sizing: border-box;
  position: relative;

  .rank-name {
    position: absolute;
    left: 0;
    top: 0;
    //height: 30px;
    line-height: 30px;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    color: #ffffff;
    background: linear-gradient(270deg, #f5b752 0%, #efd08f 99%);
    padding: 15px 50px 15px 20px;
    //margin-top: 6px;
    margin-left: 6px;
    border-bottom-right-radius: 30px;

    img {
      width: 58px;
      height: 26px;
      margin-right: 20px;
    }

    &::after {
      content: '';
      position: absolute;
      right: -13px;
      bottom: -7px;
      width: 37px;
      height: 90px;
      border-bottom-right-radius: 30px;
      background: #f5b956;
      transform: rotate(45deg);
    }
  }
}
a {
  &:hover {
    color: unset;
  }
}
.floor-item {
  height: 200px;
  border-bottom: 1px solid #eaeaea;
  margin-bottom: 20px;

  .pic {
    display: inline-block;
    margin-right: 30px;
    position: relative;
    vertical-align: top;
    border-radius: 10px;
    overflow: hidden;

    .floor-img {
      width: 230px;
      height: 170px;
    }

    img {
      //width: 230px;
      //height: 170px;
      width: 100%;
      height: 100%;
      border-radius: 10px;
    }

    .video-and-vr {
      position: absolute;
      bottom: 13px;
      left: 13px;

      .video-icon {
        width: 30px;
        height: 30px;
      }
      .vr-icon {
        width: 30px;
        height: 30px;
      }

      img {
        cursor: pointer;
      }

      .video-icon + .vr-icon {
        margin-left: 10px;
      }
    }

    .icon {
      background-size: 100% 100%;
      position: absolute;
      top: 10px;
      left: -3px;
      font-size: 13px;
      color: #ffffff;
      font-weight: bold;
      text-align: center;
      width: 52px;
      height: 26px;
      line-height: 32px;
    }

    .quality {
      background-image: url('@/assets/images/quality-icon.png');
      width: 67px;
      height: 30px;
      line-height: 33px;
      font-size: 16px;
      padding-left: 10px;
      z-index: 3;
      left: -2px;
    }

    .ranking-logo {
      position: absolute;
      top: 0px;
      left: 0px;
      height: 26px;
      line-height: 26px;
      padding: 0 0px 0 10px;
      font-size: 14px;
      font-weight: bold;
      color: #ffffff;
      background: linear-gradient(270deg, #dea445 0%, #efd08f 99%), #d8d8d8;
      z-index: 2;

      .end {
        content: '';
        position: absolute;
        right: -18px;
        bottom: -9px;
        width: 23px;
        height: 52px;
        border-bottom-right-radius: 30px;
        background: #e1aa4f;
        transform: rotate(45deg);
        z-index: 1;
      }

      img {
        border-radius: unset;
        width: 13.5px;
        height: 14px;
        position: relative;
        margin-top: -2px;
        margin-right: 5px;
      }
    }

    .opening-date {
      background-image: url('@/assets/images/opening-date-icon.png');
      z-index: 2;
    }

    .top {
      background-image: url('@/assets/images/top-icon.png');
      font-size: 18px;
      z-index: 1;
      .top-icon {
        font-size: 10px;
      }
    }

    .certificate {
      position: absolute;
      top: 0;
      right: 0;
      padding: 4px 8px;
      background: #00000033;
      box-sizing: border-box;
      color: #ffffff80;
      font-size: 8px;
      text-align: center;
    }

    .adTag {
      position: absolute;
      bottom: 0;
      left: 0;
      padding: 1px 3px;
      //width: 21px;
      //height: 11px;
      box-sizing: border-box;
      border: 0.5px solid #ffffff7f;
      color: #ffffff;
      font-size: 8px;
      text-align: center;
    }
  }

  .left-info {
    position: relative;
    display: inline-block;
    width: 420px;
    overflow: hidden;

    .title {
      font-size: 22px;
      font-weight: bold;
      margin-bottom: 10px;
    }

    .text {
      font-size: 14px;
      color: #616669;
      line-height: 33px;
      width: 420px;
      @include ell();

      .icon {
        font-size: 16px;
        margin-right: 14px;
      }
    }

    .chat {
      //在字下面加下划线
      //text-decoration: underline;
      cursor: pointer;

      .rank {
        display: inline-block;
        margin-left: 15px;
        font-size: 13px;
        font-weight: normal;
        color: #caa353;

        &.discount {
          color: #ff5b05;
        }

        img {
          // width: 16px;
          height: 20px;
          position: relative;
          top: -2px;
        }
      }
    }

    .tag-box {
      margin-top: 18px;
      position: relative;
      top: -8px;
      width: 460px;
      @include ell();
      //min-height: 22px;
      //min-width: 1px;
      //background-color: #1BB440;
    }
  }

  .right-info {
    float: right;
    text-align: right;
    width: 280px;
    height: 100%;
    position: relative;

    // 用于显示置业顾问配置的样式
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .follow {
      display: inline-block;
      font-size: 15px;
      color: #616669;
      margin-right: 10px;
    }

    .price {
      margin-top: 10px;
      font-size: 16px;
      .num {
        .n {
          color: #ff5b05;
          font-size: 24px;
          font-weight: bold;
        }
        .prefix {
          color: #2a2a33;
          font-size: 16px;
        }
      }
      &-0 {
        font-weight: bold;
        .num {
          .n {
            font-size: 30px;
          }
        }
      }
    }

    .data-info {
      text-align: left;

      .rank-data {
        width: 300px;
        font-size: 14px;
        color: #9c9c9c;
        //height: 25px;
        line-height: 25px;
        @include ell();
      }
    }

    .adviser-box {
      position: relative;
      // right: 0;
      // bottom: 25px;
      margin-top: auto;
      margin-bottom: 15px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;

      .avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        object-fit: cover;
        border: 1px solid #eaeaea;
      }

      .name {
        font-size: 14px;
        color: #3D3D3D;
      }

      .online-consult {
        font-size: 14px;
        color: #FFFFFF;
        background: #52BE54;
        padding: 0 8px;
        border-radius: 40px;
      }
    }
  }
}
a {
  &:hover {
    color: unset;
  }
}
</style>
